/*
 * This class is copied from Backstage 1.8.2.
 * We performed a lot of changes, be careful when upgrading.
 */

import {
  MainPageType,
  useLastVisitedMainPage,
  useResponsiveMobile,
  useSearchParamChangeObserver,
  useSessionQueryString,
  WbBreadcrumb,
  WbBreadcrumbs,
  WbCard,
  WbCardContent,
  WbHeader,
  WbHeaderActions,
  WbTabsListContext,
} from '@agilelab/plugin-wb-platform';
import { Content, WarningPanel } from '@mainstage/core-components';
import { EntityListProvider } from '@mainstage/plugin-catalog-react';
import { TemplateEntityV1beta3 } from '@mainstage/plugin-scaffolder-common';
import { Box, createStyles, makeStyles, Theme } from '@material-ui/core';
import { ComponentType, useEffect } from 'react';
import {
  ScaffolderPageContextProvider,
  ViewType,
} from './context/ScaffolderPageContext';
import { useScaffolderPageContext } from './context/useScaffolderPageContext';
import { ScaffolderCards } from './ScaffolderCards';
import { ScaffolderPageContextMenu } from './ScaffolderPageContextMenu';
import { ScaffolderTable } from './ScaffolderTable';

import {
  TaxonomySelector,
  useTaxonomySelection,
} from '@agilelab/plugin-wb-practice-shaper';
import { ScaffolderFilters } from './ScaffolderFilters';
import { ScaffolderViewTypeButtons } from './ScaffolderViewTypeButtons';
import { WbPage } from '@agilelab/plugin-wb-platform';

export type ScaffolderPageProps = {
  TemplateCardComponent?:
    | ComponentType<{ template: TemplateEntityV1beta3 }>
    | undefined;
  headerOptions?: {
    pageTitleOverride?: string;
    title?: string;
    subtitle?: string;
  };
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    collapse: {
      width: '100%',
    },
    wrapper: {
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing(2),
    },
    content: {
      '& > div:first-child': {
        margin: 0,
      },
    },
  }),
);

export const ScaffolderPageContents = ({
  headerOptions,
}: ScaffolderPageProps) => {
  const { error, viewType, setViewType } = useScaffolderPageContext();
  const { shouldDisable } = useTaxonomySelection();

  const [_, setLastVisitedMainPage] = useLastVisitedMainPage(
    MainPageType.BUILDER,
  );
  setLastVisitedMainPage();

  const [_sessionQueryString, setSessionQueryString] = useSessionQueryString();
  useSearchParamChangeObserver(
    (_previousSearch: string, currentSearch: string) => {
      setSessionQueryString(currentSearch);
    },
  );

  const classes = useStyles();

  const isMobile = useResponsiveMobile();

  useEffect(() => {
    if (isMobile) {
      setViewType('table');
    }
  }, [isMobile, setViewType]);

  if (error)
    return (
      <WarningPanel title="Oops! Something went wrong loading the templates">
        {error.message}
      </WarningPanel>
    );

  return (
    <WbPage themeId="home">
      <WbHeader
        pageTitleOverride="Templates"
        titleStyle={{
          display: 'flex',
          alignItems: 'center',
        }}
        title={
          <>
            Templates{' '}
            <ScaffolderPageContextMenu editor={false} actions={false} />
          </>
        }
        breadcrumbs={
          <WbBreadcrumbs aria-label="breadcrumb">
            <WbBreadcrumb>Builder</WbBreadcrumb>
            <WbBreadcrumb>Templates</WbBreadcrumb>
          </WbBreadcrumbs>
        }
        topbarActions={
          <WbHeaderActions>
            {!shouldDisable && <TaxonomySelector />}
          </WbHeaderActions>
        }
        {...headerOptions}
      />

      <WbTabsListContext
        value={viewType}
        onValueChange={v => setViewType(v as ViewType)}
      >
        <Content className={classes.content}>
          <WbCard title="Available Templates">
            <WbCardContent>
              <Box
                style={{
                  display: 'flex',
                }}
              >
                <ScaffolderFilters />
                {!isMobile && <ScaffolderViewTypeButtons />}
              </Box>

              {viewType === 'table' ? <ScaffolderTable /> : <ScaffolderCards />}
            </WbCardContent>
          </WbCard>
        </Content>
      </WbTabsListContext>
    </WbPage>
  );
};

export const ScaffolderPage = ({
  TemplateCardComponent,
  headerOptions,
}: ScaffolderPageProps) => (
  <EntityListProvider>
    <ScaffolderPageContextProvider
      TemplateCardComponent={TemplateCardComponent}
    >
      <ScaffolderPageContents headerOptions={headerOptions} />
    </ScaffolderPageContextProvider>
  </EntityListProvider>
);
